import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';

// ============================================================
// TYPE DEFINITIONS
// ============================================================

export interface ResidentInfo {
  resident_id?: number;
  user_id?: number;
  resident_full_name?: string;
  resident_email?: string;
  contact_email?: string;
  resident_contact?: string;
  community?: string;
  community_id?: number;
  building?: string;
  unit?: string;
  move_in?: string;
  expected_move_out?: string;
  is_primary?: boolean;
  is_lessee?: boolean;
  is_guarantor?: boolean;
  lease_id?: number;
}

export interface GCPetInfo {
  pet_id?: string;
  name?: string;
  pet_type?: string;
  pet_breed?: string;
  age?: number;
  weight?: number;
  color?: string;
  gender?: string;
  is_assistance_animal?: boolean;
  move_in_date?: string;
  move_out_date?: string;
  lease_id?: string;
}

export interface ResidentExistsResponse {
  exists: boolean;
  email: string;
  message: string;
}

export interface PropertyInfo {
  community: string;
  community_id?: string;
  building?: string;
  unit: string;
  is_primary?: boolean;
  lease_id?: string;
}

export interface LeaseDetails {
  lease_id: string;
  community: string;
  community_id?: string;
  building?: string;
  unit: string;
  resident_id?: string;
  resident_full_name?: string;
  move_in?: string;
  expected_move_out?: string;
  is_primary?: boolean;
  is_lessee?: boolean;
  is_guarantor?: boolean;
  last_change_at?: string;
}

export interface ResidentStats {
  in_get_covered: boolean;
  email: string;
  total_lease_records?: number;
  pet_records?: number;
  has_primary_lease?: boolean;
  primary_community?: string;
  primary_unit?: string;
  message: string;
}

// ============================================================
// API CLIENT SETUP
// ============================================================

const resolveApiBaseUrl = (): string => {
  if (typeof window !== 'undefined' && window.location && window.location.hostname === 'localhost') {
    return 'http://localhost:8000/api/';
  }
  return import.meta.env.VITE_API_URL || 'https://pawtected-client-dev-b941ac1d80e9.herokuapp.com/api/';
};

const API_BASE_URL = resolveApiBaseUrl();
console.log('[residentService] API base URL:', API_BASE_URL);

// Create axios instance with default config
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*', 
  },
});

// Add auth token to requests
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    try {
      const token = localStorage.getItem('authToken');
      
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        console.warn('⚠️ [residentService] No auth token found in localStorage');
      }
    } catch (error) {
      console.error('❌ [residentService] Error getting auth token:', error);
    }
    
    try {
      const fullUrl = `${config.baseURL || ''}${config.url || ''}`;
      console.log('[residentService] Request:', (config.method || 'get').toUpperCase(), fullUrl);
    } catch {}
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: any) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access - clear stale auth data and redirect.
      // Send to /login with a hint so the user sees a friendly "Session
      // expired, please sign in again" notice instead of a silent bounce.
      console.error('[residentService] Unauthorized access - clearing auth state and redirecting to login');
      localStorage.removeItem('authToken');
      localStorage.removeItem('totpVerified');
      localStorage.removeItem('totpVerifiedAt');
      localStorage.removeItem('totpSetupAt');
      sessionStorage.removeItem('sessionActive');
      window.location.href = '/login?hint=session-expired';
    }
    
    // Network/connectivity fallback: if localhost API is not reachable, switch to remote and retry once
    try {
      const isNetworkError = !error.response;
      const usedLocalhost = (apiClient.defaults.baseURL || '').includes('localhost:8000');
      const alreadyRetried = error.config && error.config._retriedWithRemote;
      const remoteBase = import.meta.env.VITE_API_URL || 'https://pawtected-client-dev-b941ac1d80e9.herokuapp.com/api/';
      
      if (isNetworkError && usedLocalhost && !alreadyRetried) {
        console.warn('[residentService] Local API not reachable. Switching to remote API base.');
        apiClient.defaults.baseURL = remoteBase;
        const newConfig = { ...error.config, baseURL: remoteBase, _retriedWithRemote: true };
        console.log('[residentService] Retrying:', (newConfig.method || 'get').toUpperCase(), `${newConfig.baseURL || ''}${newConfig.url || ''}`);
        return apiClient.request(newConfig);
      }
    } catch {}
    
    return Promise.reject(error);
  }
);

// ============================================================
// RESIDENT SERVICE
// ============================================================

export const residentService = {
  /**
   * Get resident information by email from Get Covered system
   * This is READ-ONLY data from unified_gc_prod table
   * 
   * @param email - Resident's email address
   * @returns Promise<ResidentInfo | null>
   */
  getResidentInfoByEmail: async (email: string): Promise<ResidentInfo | null> => {
    try {
      console.log(`📋 [residentService] Fetching resident info for email: ${email}`);
      
      const response = await apiClient.get('resident-info/by-email', {
        params: { email }
      });
      
      console.log('✅ [residentService] Resident info retrieved successfully:', response.data);
      return response.data;
      
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.log('ℹ️ [residentService] Resident not found in Get Covered system');
        return null;
      }
      
      if (error.response?.status === 403) {
        console.error('🚫 [residentService] Forbidden - Cannot access other user\'s data');
        throw new Error('You can only access your own resident information');
      }
      
      console.error('❌ [residentService] Error fetching resident info by email:', error);
      throw error;
    }
  },

  /**
   * Get resident information by authenticated user ID from Get Covered system
   * This is READ-ONLY data from unified_gc_prod table
   * 
   * @returns Promise<ResidentInfo | null>
   */
  getResidentInfoByUserId: async (): Promise<ResidentInfo | null> => {
    try {
      console.log('📋 [residentService] Fetching resident info by user ID');
      
      const response = await apiClient.get('resident-info/by-user-id');
      
      console.log('✅ [residentService] Resident info retrieved successfully:', response.data);
      return response.data;
      
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.log('ℹ️ [residentService] Resident not found in Get Covered system');
        return null;
      }
      
      console.error('❌ [residentService] Error fetching resident info by user ID:', error);
      throw error;
    }
  },

  /**
   * Check if the authenticated user exists in Get Covered system
   * Useful for determining whether to auto-populate data or allow manual entry
   * 
   * @returns Promise<ResidentExistsResponse>
   */
  checkResidentExists: async (): Promise<ResidentExistsResponse> => {
    try {
      console.log('🔍 [residentService] Checking if resident exists in Get Covered');
      
      const response = await apiClient.get('resident-info/exists');
      
      console.log(`${response.data.exists ? '✅' : '❌'} [residentService] ${response.data.message}`);
      return response.data;
      
    } catch (error) {
      console.error('❌ [residentService] Error checking resident existence:', error);
      throw error;
    }
  },

  /**
   * Get all existing pets for the resident from Get Covered system
   * These are pets already in the Get Covered database
   * 
   * @returns Promise<GCPetInfo[]>
   */
  getResidentPetsFromGC: async (): Promise<GCPetInfo[]> => {
    try {
      console.log('🐾 [residentService] Fetching pets from Get Covered');
      
      const response = await apiClient.get('resident-info/pets');
      
      console.log(`✅ [residentService] Found ${response.data.length} pet(s) in Get Covered`);
      return response.data;
      
    } catch (error) {
      console.error('❌ [residentService] Error fetching pets from Get Covered:', error);
      throw error;
    }
  },

  /**
   * Get all properties/communities associated with the authenticated user
   * 
   * @returns Promise<PropertyInfo[]>
   */
  getUserProperties: async (): Promise<PropertyInfo[]> => {
    try {
      console.log('🏢 [residentService] Fetching user properties');
      
      const response = await apiClient.get('resident-info/properties');
      
      console.log(`✅ [residentService] Found ${response.data.property_count} propert(ies)`);
      return response.data.properties || [];
      
    } catch (error) {
      console.error('❌ [residentService] Error fetching properties:', error);
      throw error;
    }
  },

  /**
   * Get detailed lease information for the authenticated user
   * 
   * @returns Promise<LeaseDetails[]>
   */
  getLeaseDetails: async (): Promise<LeaseDetails[]> => {
    try {
      console.log('📄 [residentService] Fetching lease details');
      
      const response = await apiClient.get('resident-info/lease-details');
      
      console.log(`✅ [residentService] Found ${response.data.lease_count} lease record(s)`);
      return response.data.leases || [];
      
    } catch (error) {
      console.error('❌ [residentService] Error fetching lease details:', error);
      throw error;
    }
  },
};

// ============================================================
// UTILITY FUNCTIONS
// ============================================================

/**
 * Parse phone number from Get Covered format
 * Expected format: +1234567890 or similar international format
 */
export const parsePhoneNumber = (phoneNumber: string | undefined): { countryCode: string; number: string } => {
  if (!phoneNumber) {
    return { countryCode: '+1', number: '' };
  }

  // Remove all non-digit characters except the leading +
  const cleaned = phoneNumber.trim();
  
  if (cleaned.startsWith('+')) {
    // Try to match common country codes
    const countryCodePatterns = [
      { code: '+1-CA', length: 6 },
      { code: '+1', length: 2 },
      { code: '+44', length: 3 },
      { code: '+61', length: 3 },
      { code: '+91', length: 3 },
      { code: '+86', length: 3 },
      { code: '+81', length: 3 },
      { code: '+49', length: 3 },
      { code: '+33', length: 3 },
      { code: '+39', length: 3 },
      { code: '+34', length: 3 },
      { code: '+7', length: 2 },
      { code: '+55', length: 3 },
      { code: '+52', length: 3 },
      { code: '+82', length: 3 }
    ];

    for (const pattern of countryCodePatterns) {
      if (cleaned.startsWith(pattern.code)) {
        const number = cleaned.substring(pattern.code.length).replace(/\D/g, '');
        return { countryCode: pattern.code, number };
      }
    }
  }

  // Default to +1 if no country code detected
  const digitsOnly = cleaned.replace(/\D/g, '');
  return { countryCode: '+1', number: digitsOnly };
};

/**
 * Format resident info for display
 */
export const formatResidentInfo = (info: ResidentInfo | null): string => {
  if (!info) return 'No resident information available';
  
  const parts = [];
  if (info.resident_full_name) parts.push(info.resident_full_name);
  if (info.community) parts.push(info.community);
  if (info.unit) parts.push(`Unit ${info.unit}`);
  
  return parts.join(' - ');
};

/**
 * Check if resident data is available for auto-population
 */
export const canAutoPopulate = async (): Promise<boolean> => {
  try {
    const exists = await residentService.checkResidentExists();
    return exists.exists;
  } catch (error) {
    console.error('Error checking if can auto-populate:', error);
    return false;
  }
};

// Export the service as default
export default residentService;